/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { queryOptions } from '@tanstack/react-query';
import type { Credentials } from '@opengeoweb/authentication';
import {
  getViewPreset,
  getViewPresets,
  saveViewPreset,
  saveViewPresetAs,
} from './api';
import type { ViewPreset } from '../../store/viewPresets/types';
import type { ViewPresetListItem } from '../../store/workspace/types';
import { LIB_KEY } from '../utils';

const API_KEY = 'viewpreset-api';

const generateViewPresetQueryKeys = (...queryKeys: string[]): string[] => [
  LIB_KEY,
  API_KEY,
  ...queryKeys,
];

export const viewpresetQueryOptions = {
  keys: {
    all: () => generateViewPresetQueryKeys(),
    views: () => [...viewpresetQueryOptions.keys.all(), 'view'],
    lists: () => [...viewpresetQueryOptions.keys.all(), 'list'],
  },
  getViewPreset: (id?: string, credentials?: Credentials) =>
    queryOptions({
      queryKey: [...viewpresetQueryOptions.keys.views(), id],
      queryFn: async (): Promise<ViewPreset> => getViewPreset(id, credentials),
    }),
  getViewPresets: (credentials?: Credentials) =>
    queryOptions({
      queryKey: viewpresetQueryOptions.keys.lists(),
      queryFn: async (): Promise<ViewPresetListItem[]> =>
        getViewPresets(credentials),
      staleTime: 2 * 60 * 1000,
      refetchOnMount: true,
    }),
  saveViewPreset: (id: string, data: ViewPreset, credentials?: Credentials) =>
    queryOptions({
      queryKey: [...viewpresetQueryOptions.keys.views(), id, 'save'],
      staleTime: 0,
      queryFn: async (): Promise<void> => saveViewPreset(id, data, credentials),
    }),
  saveViewPresetAs: (data: ViewPreset, credentials?: Credentials) =>
    queryOptions({
      queryKey: [...viewpresetQueryOptions.keys.views(), 'save-as'],
      staleTime: 0,
      queryFn: async (): Promise<string> => saveViewPresetAs(data, credentials),
    }),
} as const;
